import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, LeaderboardEntry, SalespersonMetrics } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface TopPerformersChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

interface PerformerScore {
  name: string;
  compositeIndex: number;
  sopScore: number;
  interestScore: number;
  objectionScore: number;
  meetingsNorm: number;
  talkBalanceScore: number;
}

const computeCompositeIndex = (
  entry: LeaderboardEntry,
  metrics: SalespersonMetrics | null,
  maxConversations: number
): PerformerScore => {
  const sopScore = entry.avg_sop_score || 0;
  const interestScore = entry.avg_buyer_interest_score || 0;
  const objectionScore = metrics?.objection_handling_score || 0;
  const talkPct = metrics?.average_talk_percentage || 50;

  // Normalize meetings: min(total / max * 100, 100)
  const meetingsNorm = maxConversations > 0
    ? Math.min((entry.total_conversations / maxConversations) * 100, 100)
    : 0;

  // Talk balance: ideal is 50/50; penalize deviation
  const talkBalanceScore = Math.max(0, 100 - Math.abs(50 - talkPct) * 2);

  // Composite: 0.30 SOP + 0.25 Interest + 0.20 Objection + 0.15 Meetings + 0.10 TalkBalance
  const compositeIndex =
    0.30 * sopScore +
    0.25 * interestScore +
    0.20 * objectionScore +
    0.15 * meetingsNorm +
    0.10 * talkBalanceScore;

  return {
    name: entry.salesperson_name,
    compositeIndex: Math.round(compositeIndex * 10) / 10,
    sopScore,
    interestScore,
    objectionScore,
    meetingsNorm: Math.round(meetingsNorm * 10) / 10,
    talkBalanceScore: Math.round(talkBalanceScore * 10) / 10,
  };
};

const TopPerformersChart: React.FC<TopPerformersChartProps> = ({
  businessIdentifier,
  timeRange = '7d',
}) => {
  const [performers, setPerformers] = useState<PerformerScore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const leaderboard = await analyticsService.getSalesLeaderboard(
          businessIdentifier,
          timeRange,
          'total_conversations',
          20
        );
        const entries = leaderboard.leaderboard || [];

        if (entries.length === 0) {
          setPerformers([]);
          return;
        }

        const maxConversations = Math.max(...entries.map((e) => e.total_conversations), 1);

        // Fetch per-rep metrics
        const metricsResults = await Promise.allSettled(
          entries.map((entry) => analyticsService.getSalespersonMetrics(entry.salesperson_id))
        );

        const metricsMap = new Map<string, SalespersonMetrics>();
        metricsResults.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            metricsMap.set(entries[idx].salesperson_id, result.value);
          }
        });

        const scored = entries.map((entry) => {
          const metrics = metricsMap.get(entry.salesperson_id) || null;
          return computeCompositeIndex(entry, metrics, maxConversations);
        });

        // Sort descending by composite index
        scored.sort((a, b) => b.compositeIndex - a.compositeIndex);

        setPerformers(scored);
      } catch (err: any) {
        console.error('[TopPerformersChart] Error fetching data:', err);
        setError(err.message || 'Failed to load top performers data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (performers.length === 0) return {};

    const names = performers.map((p) => p.name);
    const scores = performers.map((p) => p.compositeIndex);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const perf = performers[idx];
          if (!perf) return '';
          return [
            `<b>${perf.name}</b>`,
            `Composite Score: <b>${perf.compositeIndex}</b>`,
            `SOP: ${perf.sopScore.toFixed(1)} | Interest: ${perf.interestScore.toFixed(1)}`,
            `Objection: ${perf.objectionScore.toFixed(1)} | Meetings: ${perf.meetingsNorm.toFixed(1)}`,
            `Talk Balance: ${perf.talkBalanceScore.toFixed(1)}`,
          ].join('<br/>');
        },
      },
      grid: {
        left: '3%',
        right: '10%',
        bottom: '3%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: names,
        inverse: true,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          width: 90,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          type: 'bar',
          data: scores.map((score, idx) => ({
            value: score,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  {
                    offset: 0,
                    color: idx < 3 ? '#FFD700' : CHART_COLORS.primary,
                  },
                  {
                    offset: 1,
                    color: idx < 3 ? '#FFA500' : CHART_COLORS.primary + 'CC',
                  },
                ],
              },
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'right',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            fontWeight: 'bold',
            formatter: (params: any) => params.value?.toFixed(1),
          },
        },
      ],
    };
  };

  return (
    <ChartCard
      title="Top Performers"
      subtitle="Composite performance index (SOP 30% + Interest 25% + Objection 20% + Meetings 15% + Talk Balance 10%)"
      isLoading={isLoading}
      error={error}
    >
      {performers.length === 0 && !isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">No data available</Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: Math.max(300, performers.length * 32) + 'px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default TopPerformersChart;
